import {
  formatE1rm,
  formatVolume,
  formatWeight,
  type Point,
  type TrackerRange,
  type TrackerSession,
  type TrackerStat,
  type UnitsPreference,
  type WeightUnitLabels,
} from "@heraion/shared";

import type { ChartSeries } from "../charts/chartAnnotations";
import type { LineStroke } from "../charts/chartDots";
import type { TFn } from "../i18nTypes";

// #389: the exercise tracker's charts, pure. One chart per exercise, one
// curve per statistic, all in one colour and told apart by their line. The
// y axis is the change since the first session in the range, so a kilo and
// a rep share it; a tap reads the real values.

/** Each statistic's line. Fixed, so the key never reshuffles on a toggle. */
export const TRACKER_STAT_STROKE: Record<TrackerStat, LineStroke> = {
  e1rm: "solid",
  volume: "longDash",
  topWeight: "dotted",
  topReps: "dashDot",
};

export function trackerStatLabel(t: TFn, stat: TrackerStat): string {
  switch (stat) {
    case "e1rm":
      return t("mobile.progress.tracker.stat.e1rm", { defaultValue: "e1RM" });
    case "topWeight":
      return t("mobile.progress.tracker.stat.topWeight", { defaultValue: "Top set" });
    case "volume":
      return t("mobile.progress.tracker.stat.volume", { defaultValue: "Volume" });
    case "topReps":
      return t("mobile.progress.tracker.stat.topReps", { defaultValue: "Top set reps" });
  }
}

/** A range chip's text, short so the four fit one row at 320. */
export function rangeLabel(t: TFn, r: TrackerRange): string {
  switch (r) {
    case "30d":
      return t("mobile.progress.tracker.range.30d", { defaultValue: "30d" });
    case "12w":
      return t("mobile.progress.tracker.range.12w", { defaultValue: "12w" });
    case "6m":
      return t("mobile.progress.tracker.range.6m", { defaultValue: "6m" });
    case "12m":
      return t("mobile.progress.tracker.range.12m", { defaultValue: "12m" });
  }
}

/** What a screen reader says for a range chip. */
export function rangeA11y(t: TFn, r: TrackerRange): string {
  switch (r) {
    case "30d":
      return t("mobile.progress.tracker.rangeA11y.30d", { defaultValue: "Last 30 days" });
    case "12w":
      return t("mobile.progress.tracker.rangeA11y.12w", { defaultValue: "Last 12 weeks" });
    case "6m":
      return t("mobile.progress.tracker.rangeA11y.6m", { defaultValue: "Last 6 months" });
    case "12m":
      return t("mobile.progress.tracker.rangeA11y.12m", { defaultValue: "Last 12 months" });
  }
}

export type TrackerFormat = {
  t: TFn;
  units: UnitsPreference;
  locale: string;
  labels: WeightUnitLabels;
  /** Whole numbers in the locale, built once (trackerFormat). */
  integer: Intl.NumberFormat;
};

/** What formatTrackerValue reads, built once per locale and unit. */
export function trackerFormat(
  t: TFn,
  units: UnitsPreference,
  locale: string,
  labels: WeightUnitLabels,
): TrackerFormat {
  return { t, units, locale, labels, integer: new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }) };
}

/** How a stroke reads aloud: the key's a11y label names each curve's line. */
export function lineStyleLabel(t: TFn, stroke: LineStroke): string {
  switch (stroke) {
    case "solid":
      return t("mobile.progress.tracker.line.solid", { defaultValue: "solid line" });
    case "dashed":
    case "longDash":
      return t("mobile.progress.tracker.line.dashed", { defaultValue: "dashed line" });
    case "dotted":
      return t("mobile.progress.tracker.line.dotted", { defaultValue: "dotted line" });
    case "dashDot":
      return t("mobile.progress.tracker.line.dashDot", { defaultValue: "dash-dot line" });
  }
}

/** A statistic's real value, in the athlete's unit. */
export function formatTrackerValue(stat: TrackerStat, value: number, f: TrackerFormat): string {
  switch (stat) {
    case "e1rm":
      return formatE1rm(value, f.units, f.locale, f.labels);
    case "topWeight":
      return formatWeight(value, f.units, f.locale, f.labels);
    case "volume":
      return formatVolume(value, f.units, f.locale, f.labels);
    case "topReps":
      // The label already says reps: «Top set reps 8».
      return f.integer.format(value);
  }
}

export type TrackerChart = {
  series: ChartSeries[];
  /** The points a tap walks: the curve with the most sessions. */
  headline: Point[];
};

/** One curve per statistic with at least one session, as the percent change
 *  from its first session in the range. The curve with the most sessions is
 *  the one a tap reads (ties: the first in `stats`). */
export function buildTrackerChart(
  sessions: readonly TrackerSession[],
  stats: readonly TrackerStat[],
  t: TFn,
  dateLabel: (atMs: number) => string,
): TrackerChart {
  const curves = stats.flatMap((stat) => {
    // Anchored on the first value above zero: a 0 would divide by zero.
    const own = sessions.filter((s) => s.values[stat] !== undefined);
    const first = own.find((s) => (s.values[stat] ?? 0) > 0)?.values[stat];
    if (first === undefined) return [];
    const points = own.map((s) => ({
      x: s.atMs,
      y: ((s.values[stat] ?? first) / first - 1) * 100,
      label: dateLabel(s.atMs),
    }));
    return [{ stat, points }];
  });
  const lead = curves.reduce<(typeof curves)[number] | undefined>(
    (best, c) => (best === undefined || c.points.length > best.points.length ? c : best),
    undefined,
  );
  const series: ChartSeries[] = curves.map((c) => ({
    key: c.stat,
    points: c.points,
    variant: "primary",
    stroke: TRACKER_STAT_STROKE[c.stat],
    headline: c === lead,
    name: trackerStatLabel(t, c.stat),
  }));
  return { series, headline: lead?.points ?? [] };
}

/** What the head says for a session: its date, then each statistic it
 *  carries with its real value, in the key's order. `stacked`: one line
 *  each, the date first (many statistics, or large text); else one line
 *  joined by « · ». A label and its value never part (a no-break space in
 *  `statValue`). */
export function trackerReadout(
  session: TrackerSession | undefined,
  stats: readonly TrackerStat[],
  f: TrackerFormat,
  dateLabel: (atMs: number) => string,
  stacked = false,
): string | undefined {
  if (session === undefined) return undefined;
  const parts = stats.flatMap((stat) => {
    const v = session.values[stat];
    return v === undefined
      ? []
      : [
          f.t("mobile.progress.tracker.statValue", {
            stat: trackerStatLabel(f.t, stat),
            value: formatTrackerValue(stat, v, f),
            defaultValue: "{{stat}} {{value}}",
          }),
        ];
  });
  return [dateLabel(session.atMs), ...parts].join(stacked ? "\n" : " · ");
}

/** The percent change on the y axis, signed. No `signDisplay` or percent
 *  style: Hermes' Intl is not trusted with them. */
export function percentFormat(t: TFn, locale: string): (y: number) => string {
  const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  return (y) => {
    const n = Math.round(y);
    // `|| 0`: a -0 would print «-0».
    const value = n > 0 ? `+${fmt.format(n)}` : fmt.format(n || 0);
    return t("mobile.progress.tracker.change", { value, defaultValue: "{{value}}%" });
  };
}
